import { Link } from "@inertiajs/react"
import { Award, Check, ClipboardCheck, Lock } from "lucide-react"
import { cn } from "@/lib/utils"

// A guided track's step list (plan 2026-09-23-002). Shared by the path
// overview and the track player. The step holding the open lesson — else the
// current step — shows its lessons nested underneath. Locks are enforced on
// the server; this only draws them.

const STATE_LABEL = { done: "Complete", current: "Up next", open: "Open", locked: "Locked" }

function StepDot({ step }) {
  return (
    <span
      className={cn(
        "z-10 h-7 w-7 rounded-full flex items-center justify-center text-xs font-mono shrink-0 border-2",
        step.state === "done" && "bg-green-600 border-green-600 text-white",
        step.state === "current" && "bg-kf-red border-kf-red text-white",
        step.state === "open" && "bg-white border-zinc-400 text-zinc-700",
        step.state === "locked" && "bg-white border-zinc-300 text-zinc-400"
      )}
    >
      {step.state === "done" ? <Check className="h-3.5 w-3.5" /> : step.state === "locked" ? <Lock className="h-3 w-3" /> : step.step}
    </span>
  )
}

export function TrackProgress({ progress, className }) {
  const pct = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0
  return (
    <div className={className}>
      <div
        className="h-2 bg-zinc-200 rounded-full overflow-hidden"
        role="progressbar"
        aria-valuenow={progress.completed}
        aria-valuemin={0}
        aria-valuemax={progress.total}
      >
        <div className="h-full bg-kf-red rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-zinc-500 mt-1">
        {progress.completed} of {progress.total} steps complete
      </p>
    </div>
  )
}

function stepHref(step) {
  if (step.state === "locked" || step.lessons.length === 0) return null
  return (step.lessons.find((l) => !l.completed) || step.lessons[0]).href
}

export function TrackRail({ track, steps, progress, activeLessonId = null, certified = false, overviewHref = null, onNavigate }) {
  const activeStep = steps.find((s) => s.lessons.some((l) => l.id === activeLessonId)) || steps.find((s) => s.state === "current")

  return (
    <nav aria-label="Path steps" className="space-y-4 min-w-0">
      <div className="min-w-0">
        {overviewHref ? (
          <Link href={overviewHref} onClick={onNavigate} className="font-display text-lg font-semibold leading-tight text-zinc-900 hover:text-kf-red">
            {track.title}
          </Link>
        ) : (
          <h1 className="font-display text-xl font-semibold leading-tight text-zinc-900">{track.title}</h1>
        )}
        <TrackProgress progress={progress} className="mt-3" />
      </div>

      <ol className="relative">
        <div className="absolute left-[13px] top-4 bottom-4 w-px bg-zinc-200" aria-hidden="true" />
        {steps.map((step) => {
          const href = stepHref(step)
          const expanded = activeStep && step.courseId === activeStep.courseId
          const row = (
            <>
              <StepDot step={step} />
              <span className="min-w-0">
                <span className={cn("block text-sm leading-tight truncate", expanded ? "font-semibold text-zinc-900" : "text-zinc-700")}>
                  {step.title}
                </span>
                <span className="block text-[11px] text-zinc-400">{STATE_LABEL[step.state]}</span>
              </span>
            </>
          )
          return (
            <li key={step.courseId} className="min-w-0">
              {href ? (
                <Link href={href} onClick={onNavigate} className={cn("relative flex items-center gap-3 w-full py-2 pr-2 rounded-md min-h-[44px]", expanded && "bg-white shadow-sm")}>
                  {row}
                </Link>
              ) : (
                <div className="relative flex items-center gap-3 w-full py-2 pr-2 min-h-[44px] opacity-60">{row}</div>
              )}
              {expanded && step.lessons.length > 0 && (
                <ul className="ml-10 mt-1 mb-2 space-y-0.5">
                  {step.lessons.map((lesson) => (
                    <li key={lesson.id}>
                      <Link
                        href={lesson.href}
                        onClick={onNavigate}
                        className={cn(
                          "flex items-center gap-2 rounded px-2 py-1.5 text-[13px] min-h-[44px]",
                          lesson.id === activeLessonId ? "bg-kf-red/10 text-kf-red font-medium" : "text-zinc-600 hover:bg-zinc-100"
                        )}
                      >
                        {lesson.completed ? (
                          <Check className="h-3.5 w-3.5 text-green-600 shrink-0" />
                        ) : (
                          <span className="h-3 w-3 rounded-full border border-zinc-300 shrink-0" />
                        )}
                        <span className="truncate">{lesson.title}</span>
                        {lesson.quiz && <ClipboardCheck className="h-3.5 w-3.5 text-amber-600 shrink-0 ml-auto" />}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )
        })}
        {track.certifying && (
          <li className="relative flex items-center gap-3 py-2">
            <span className={cn("z-10 h-7 w-7 rounded-full flex items-center justify-center shrink-0", certified ? "bg-green-600 text-white" : "bg-zinc-200 text-zinc-500")}>
              <Award className="h-4 w-4" />
            </span>
            <span className={cn("text-sm font-semibold", certified ? "text-green-700" : "text-zinc-400")}>Certificate</span>
          </li>
        )}
      </ol>
    </nav>
  )
}
